import { useTranslate } from '../../application/i18n/LocaleProvider';
import { praiseKey } from '../../domain/game/duelPraise';
import { speedMultiplier } from '../../domain/game/duelScoring';

/**
 * The word the round is answered with, slammed over the whole screen
 * (SPEC-007 D-60).
 *
 * The reveal explains the answer and the outcome line names it, and between them
 * they were doing a careful job of telling a reader something they wanted to
 * FEEL. A run of right answers should read as a run, and a lost one should land
 * — so the moment of the answer gets a hero: one word, the display face,
 * the full width of the column, in over a beat and gone.
 *
 * IT ROTATES, and that is what makes it a run rather than a repetition. Which
 * word belongs to which streak is `domain/game/duelPraise`, deterministic and
 * pinned by a test; this component only draws it.
 *
 * THE POINTS COME WITH IT (SPEC-007 D-62). "+200" was a line of caption beside
 * the outcome in the reveal's header, which is the right size for a ledger and
 * the wrong size for the thing that just happened. It rides the hero instead,
 * landing a beat after the word, and the score rolling up at the top of the
 * screen is where the number goes to stay.
 *
 * AND THE MULTIPLIER RIDES THE POINTS (SPEC-007 D-76). A round answered under
 * two seconds is worth double and under one is worth triple, and a threshold is
 * only a reward if the reader is told they crossed it — a payment that quietly
 * said 200 instead of 100 is a rule nobody can learn from playing. So the tier
 * is drawn ON the payment, in the ramp, thrown in a beat behind the number it
 * multiplied and overshooting the way the word above it does.
 *
 * BESIDE THE NUMBER, NOT UNDER IT, which is the same rule D-74 moved the
 * reaction out of this component for: the hero is one thing said loudly, and a
 * stack of figures under a word that big is a paragraph. `+300 ×3` is one figure
 * with its reason attached, on one line, and it is the only thing this band has
 * gained since.
 *
 * AN ORDINARY ROUND DRAWS NOTHING. "×1" is not a reward, it is the screen
 * telling somebody they were ordinary — and it would put a badge on the hero of
 * every round, which is a badge that has stopped meaning anything.
 *
 * THE REACTION DOES NOT (SPEC-007 D-74). It rode here as a third line for a
 * revision — what the round paid, and under it what bought it — and the number
 * is right where the placement was not: the hero is ONE thing said loudly, and a
 * stack of three figures under a word that big is a paragraph. It reads better
 * against the counter it explains anyway, which is where it went.
 *
 * WHAT THE PAYMENT LEFT IS SAID AT THE TOP OF THE SCREEN TOO (D-63), and for
 * the same reason: it was a third line here once, and a balance is not the one
 * thing this band says loudly. The ladder band carries it, which puts the gap
 * where the gap already lives.
 *
 * DECORATIVE, and the two things that follow from that are not optional. It is
 * `aria-hidden`, because the reveal below is a live region that already
 * announces the outcome and a screen reader given both would hear the round
 * twice. And it is `pointer-events: none` over its whole life, because it covers
 * the two cards and the primary action — a reader tapping Next through the tail
 * of an animation must never be tapping this.
 *
 * It is not unmounted on a timer. The animation ends at `opacity: 0` with a
 * `both` fill, so the element simply stops being visible and the screen owes it
 * nothing; a timer would be a second clock on a screen that already has one, and
 * one that could fire into a round that had already moved on.
 */
export default function DuelHero({
    outcome,
    streak,
    points = 0,
    reaction = null,
}) {
    const t = useTranslate();

    if (outcome !== 'correct' && outcome !== 'over') {
        return null;
    }

    // The tier this round landed in, and it is asked of the reaction rather than
    // divided back out of the points: the domain owns which interval is worth
    // what, and a component that derived it from the payment would be a second
    // copy of the thresholds.
    const multiplier = outcome === 'correct' ? speedMultiplier(reaction) : 1;

    return (
        <p className={`duel-hero duel-hero--${outcome}`} aria-hidden="true">
            <span className="duel-hero__word">
                {t(outcome === 'correct' ? praiseKey(streak) : 'duel.hero.lost')}
            </span>

            {/* What the round paid, under the word that says it was right and a
                beat behind it: the two are one sentence — correct, and this
                fast — and they used to be a line of caption in the reveal's
                header. A round that paid nothing prints nothing rather than a
                zero. */}
            {points > 0 ? (
                <span className="duel-hero__payment">
                    <span className="duel-hero__points figure">{t('duel.points', { count: points })}</span>

                    {/* Keyed on the tier so the badge re-enters when it changes
                        — a CSS entrance only plays for an element that is new,
                        and two quick rounds in a row would otherwise land the
                        first one's animation and then nothing. */}
                    {multiplier > 1 ? (
                        <span
                            className={`duel-hero__multiplier duel-hero__multiplier--${multiplier}`}
                            key={multiplier}
                        >
                            {t('duel.multiplier', { times: multiplier })}
                        </span>
                    ) : null}
                </span>
            ) : null}

        </p>
    );
}
